/*
 * Purpose: resolves real Shopify variants for multiple inline Drop product forms and updates their presentation.
 * Dependencies: Galleria's base component and product-form custom element; no cart or bundle logic is duplicated here.
 * Rendered by: snippets/drop-product-showcase.liquid on the active Drop homepage.
 * Related data: Shopify product variants serialized by snippets/drop-product-form.liquid.
 */

(async () => {
  const componentName = 'drop-product-purchase';

  if (!window.customElements || customElements.get(componentName)) return;

  const baseComponentUrl = window.theme?.modules?.baseComponent;
  if (!baseComponentUrl) {
    console.warn('Drop product forms could not initialize because the Galleria base component is unavailable.');
    return;
  }

  try {
    const baseComponent = await import(baseComponentUrl);

    if (customElements.get(componentName)) return;

    customElements.define(componentName, class extends baseComponent.default {
      render() {
        this.optionSelectors = Array.from(this.querySelectorAll('[data-drop-option]'));
        this.variantInput = this.querySelector('[data-variant-id-input]');
        this.productForm = this.querySelector('product-form');
        this.addButton = this.querySelector('[data-add-button]');
        this.soldOutButton = this.querySelector('[data-sold-out-button]');
        this.soldOutButtonText = this.soldOutButton?.querySelector('.\\#button-text');
        this.status = this.querySelector('[data-drop-variant-status]');
        this.currentPrice = this.querySelector('[data-drop-price-current]');
        this.comparePrice = this.querySelector('[data-drop-price-compare]');
        this.savings = this.querySelector('[data-drop-price-savings]');
        this.image = this.querySelector('[data-drop-product-media] img');
        this.detailsTemplate = this.querySelector('[data-drop-product-details]');
        this.showSavings = this.dataset.showSavings === 'true';
        this.purchaseEnabled = this.dataset.purchaseEnabled === 'true';
        this.purchaseDisabledLabel = this.dataset.purchaseDisabledLabel || 'Purchasing is not available';
        this.soldOutLabel = window.theme?.i18n?.soldOut || 'Sold out';
        this.unavailableLabel = window.theme?.i18n?.unavailable || 'Unavailable';
        this.variants = this._readVariants();

        if (!this.variants.length || !this.variantInput) {
          this._disablePurchaseInterface(this.unavailableLabel);
          return;
        }

        this.handleOptionChange = this._handleOptionChange.bind(this);
        this.optionSelectors.forEach((selector) => {
          selector.disabled = false;
          selector.addEventListener('change', this.handleOptionChange);
        });

        const initialVariantId = Number(this.getAttribute('initial-variant-id'));
        const initialVariant = this.variants.find((variant) => variant.id === initialVariantId) || this.variants[0];
        this._selectVariant(initialVariant, false);
      }

      _readVariants() {
        const variantData = this.querySelector('[data-drop-product-variants]');
        if (!variantData) return [];

        try {
          const variants = JSON.parse(variantData.textContent);
          if (!Array.isArray(variants)) throw new TypeError('Variant data must be an array.');

          return variants.filter((variant) => {
            return variant && variant.id !== undefined && Array.isArray(variant.options);
          });
        } catch (error) {
          console.warn('Drop product variants could not be parsed.', error);
          return [];
        }
      }

      _handleOptionChange(event) {
        const changedIndex = Number(event.currentTarget.dataset.optionIndex);
        if (!Number.isInteger(changedIndex) || changedIndex < 0) return;

        const selectedPrefix = this.optionSelectors
          .slice(0, changedIndex + 1)
          .map((selector) => selector.value);

        const matchingAvailableVariant = this.variants.find((variant) => {
          return variant.available && selectedPrefix.every((value, index) => variant.options[index] === value);
        });
        const matchingVariant = matchingAvailableVariant || this.variants.find((variant) => {
          return selectedPrefix.every((value, index) => variant.options[index] === value);
        });

        if (matchingVariant) this._selectVariant(matchingVariant);
      }

      _selectVariant(variant, updateImage = true) {
        if (!variant) return;

        this.currentVariant = variant;
        this.variantInput.value = variant.id;
        this.variantInput.disabled = false;
        this.setAttribute('current-variant-id', variant.id);
        this.productForm?.setAttribute('current-variant', variant.id);

        this._syncOptionSelectors(variant);
        this._updateOptionAvailability();
        this._updatePurchaseButtons(variant);
        this._updatePrice(variant);
        this._updateDetails(variant);
        if (updateImage) this._updateImage(variant);
        this._announceVariant(variant);
      }

      _syncOptionSelectors(variant) {
        this.optionSelectors.forEach((selector, index) => {
          if (variant.options[index] !== undefined) selector.value = variant.options[index];
        });
      }

      _updateOptionAvailability() {
        const selectedOptions = this.optionSelectors.map((selector) => selector.value);

        this.optionSelectors.forEach((selector, optionIndex) => {
          const selectedPrefix = selectedOptions.slice(0, optionIndex);

          Array.from(selector.options).forEach((option) => {
            option.disabled = !this.variants.some((variant) => {
              const prefixMatches = selectedPrefix.every((value, index) => variant.options[index] === value);
              return variant.available && prefixMatches && variant.options[optionIndex] === option.value;
            });
          });
        });
      }

      _updatePurchaseButtons(variant) {
        const available = Boolean(variant.available) && this.purchaseEnabled;
        this.addButton?.toggleAttribute('hidden', !available);
        this.soldOutButton?.toggleAttribute('hidden', available);
        if (this.addButton) this.addButton.disabled = !available;
      }

      _updatePrice(variant) {
        if (this.currentPrice) this.currentPrice.textContent = variant.price;

        if (this.comparePrice) {
          this.comparePrice.textContent = variant.compareAtPrice || '';
          this.comparePrice.toggleAttribute('hidden', !variant.compareAtPrice);
        }

        if (this.savings) {
          const showSavings = this.showSavings && variant.savingsLabel;
          this.savings.textContent = showSavings ? variant.savingsLabel : '';
          this.savings.toggleAttribute('hidden', !showSavings);
        }
      }

      _updateImage(variant) {
        if (!this.image || !variant.featuredImage) return;

        this.image.removeAttribute('srcset');
        this.image.removeAttribute('sizes');
        this.image.src = variant.featuredImage;
        if (variant.featuredImageAlt) this.image.alt = variant.featuredImageAlt;
      }

      _updateDetails(variant) {
        const details = this.detailsTemplate?.content || this;

        const price = details.querySelector('[data-drop-details-price]');
        const comparePrice = details.querySelector('[data-drop-details-compare]');
        const sku = details.querySelector('[data-drop-details-sku]');
        const availability = details.querySelector('[data-drop-details-availability]');
        const image = details.querySelector('[data-drop-details-image]');
        const modalPurchase = details.querySelector('[data-drop-modal-purchase]');

        modalPurchase?.setAttribute('initial-variant-id', variant.id);
        if (price) price.textContent = variant.price;
        if (comparePrice) {
          comparePrice.textContent = variant.compareAtPrice || '';
          comparePrice.toggleAttribute('hidden', !variant.compareAtPrice);
        }
        if (sku) sku.textContent = variant.sku || 'Not provided';
        if (availability) availability.textContent = variant.available ? 'Available' : this.soldOutLabel;
        if (image && variant.featuredImage) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = variant.featuredImage;
          if (variant.featuredImageAlt) image.alt = variant.featuredImageAlt;
        }
      }

      _announceVariant(variant) {
        if (!this.status) return;
        if (!this.purchaseEnabled) {
          this.status.textContent = this.purchaseDisabledLabel;
          return;
        }

        this.status.textContent = variant.available ? 'Available' : this.soldOutLabel;
      }

      _disablePurchaseInterface(label) {
        this.optionSelectors.forEach((selector) => {
          selector.disabled = true;
        });
        this.addButton?.toggleAttribute('hidden', true);
        this.soldOutButton?.toggleAttribute('hidden', false);
        if (this.addButton) this.addButton.disabled = true;
        if (this.soldOutButtonText) this.soldOutButtonText.textContent = label;
        if (this.status) this.status.textContent = label;
      }
    });
  } catch (error) {
    console.warn('Drop product forms could not initialize.', error);
  }
})();
